import I18n from 'i18n-js';
import React, { useState } from 'react';
import SlideShowCard from '@/components/shared/NewLessonOverview/Cards/SlideShowCard';
import SlideGroupCard from '@/components/shared/NewLessonOverview/Cards/SlideGroupCard';
import Grid from '@/components/shared/NewLessonOverview/Cards/Grid';
import Cookie from 'react-cookies';
import useFlags from '@/hooks/useFlags';
import ReadingCard from '../Cards/ReadingCard';
import useLessonOverviewContext from '../useLessonOverviewContext';
import ShowSlideshowCheckbox from '../ShowSlideshowCheckbox';
import LessonSubheader from '../LessonSubheader';
import { shouldRenderSlideShow } from './utils';

type LessonOptionsProps = {
  subheaderNumber?: number;
};

const LessonOptions = ({ subheaderNumber }: LessonOptionsProps) => {
  const firstTCISlideShow = useLessonOverviewContext(state => state.firstTCISlideShow());
  const newTCISlideShows = useLessonOverviewContext(state => state.newTCISlideShows());
  const oldTCISlideShows = useLessonOverviewContext(state => state.oldTCISlideShows());
  const teacherSlideShows = useLessonOverviewContext(state => state.teacherSlideShows());
  const slideGroups = useLessonOverviewContext(state => state.lessonOptionsSlideGroups());
  const sections = useLessonOverviewContext(state => state.sections);
  const isStaffer = useLessonOverviewContext(state => state.isStaffer);

  const slideShowsToRender = [...newTCISlideShows, ...oldTCISlideShows];
  const { bigBookSlideshow } = useFlags();
  const hasReadingActivityCard = slideShowsToRender.some(s => s.activityType === 'reading_activity')
    && bigBookSlideshow;
  const readingCardSections = hasReadingActivityCard ? []
    : sections.filter(s => s.kind === 'default' && s.investigation === false);
  const showReadingCard = readingCardSections.length > 0;
  const slideGroupsWithThumbnails = slideGroups?.filter(slideGroup => slideGroup.thumbnail !== '/missing.png');
  const slideGroupsWithoutThumbnails = slideGroups?.filter(slideGroup => slideGroup.thumbnail === '/missing.png');
  const lessonOptionsColor = '#522E91';

  const [showTeacherSlideShows, setShowTeacherSlideShows] = useState(Cookie.load('showTeacherSlideshows') !== 'false');

  const handleShowTeacherSlideShowsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowTeacherSlideShows(event.target.checked);
    Cookie.save('showTeacherSlideshows', event.target.checked.toString(), { path: '/' });
  };

  return (
    <LessonSubheader subheaderNumber={subheaderNumber} title={I18n.t('choose_a_lesson')}>
      {isStaffer && (
        <ShowSlideshowCheckbox
          handleCheckboxChange={handleShowTeacherSlideShowsChange}
          isChecked={showTeacherSlideShows}
        />
      )}

      <Grid>
        {firstTCISlideShow && slideGroupsWithThumbnails.map(slideGroup => (
          <SlideGroupCard
            key={slideGroup.id}
            truncateTextTooltip
            iconColor={lessonOptionsColor}
            slideGroup={slideGroup}
          />
        ))}

        {slideShowsToRender.map((slideShow) => {
          if (!shouldRenderSlideShow(slideShow, bigBookSlideshow)) return null;

          return (
            <div key={slideShow.id} className="tw-row-span-2">
              <SlideShowCard truncateTextTooltip iconColor={lessonOptionsColor} slideShow={slideShow} />
            </div>
          );
        })}

        {showReadingCard && (
          <ReadingCard
            truncateTextTooltip
            iconColor={lessonOptionsColor}
            sections={readingCardSections}
          />
        )}

        {slideGroupsWithoutThumbnails.map(slideGroup => (
          <SlideGroupCard
            key={slideGroup.id}
            truncateTextTooltip
            iconColor={lessonOptionsColor}
            slideGroup={slideGroup}
          />
        ))}
      </Grid>

      {isStaffer && showTeacherSlideShows && teacherSlideShows?.length > 0 && (
        <Grid className="tw-mt-4">
          {teacherSlideShows.map((slideShow) => {
            if (!shouldRenderSlideShow(slideShow, bigBookSlideshow)) return null;

            return (
              <SlideShowCard
                key={slideShow.id}
                isTeacherSlideShow
                truncateTextTooltip
                iconColor={lessonOptionsColor}
                slideShow={slideShow}
              />
            );
          })}
        </Grid>
      )}

      {!isStaffer && teacherSlideShows?.length > 0 && (
        <Grid className="tw-mt-4">
          {teacherSlideShows.map(slideShow => (
            <SlideShowCard
              key={slideShow.id}
              isTeacherSlideShow
              truncateTextTooltip
              iconColor={lessonOptionsColor}
              slideShow={slideShow}
            />
          ))}
        </Grid>
      )}
    </LessonSubheader>
  );
};

export default LessonOptions;
